/**
 * Your turn in an imagined scene (DOM-free, tested in scene-turns.test.ts): once every word the moment screen marks
 * in the learner's own lines is out of the fog, the scene becomes a role-play where they say those lines themselves.
 * The other person's lines play in order; at each of the learner's the role-play stops for them to say it. Unscored:
 * it moves no box.
 */
import { isYou } from './scene-lines';

/** One line of the role-play, in the scene's order: `you` for a line the learner says. */
export interface TurnStep { line: number; you: boolean }

/** The role-play's steps: every line of the scene, in order, the learner's marked. */
export function turnSteps(speakers: readonly (string | undefined)[]): TurnStep[] {
  return speakers.map((s, line) => ({ line, you: isYou(s) }));
}

/** A scene line as the turn reads it: its speaker and, when its blueprint matches it, the words marked in it. */
export interface TurnLine {
  speaker?: string;
  parts?: readonly { key?: string }[];
  latinParts?: readonly { key?: string }[];
  patterns?: readonly { key: string }[];
}

/**
 * Whether the turn is open: every word marked in the learner's lines (a blueprint part with a key, a pattern the
 * words list marks) is out of the fog, and there is at least one. Glue words are never marked, so they never hold it,
 * and a learner's line with no marked word holds nothing. `words` counts the marked words (each once) and `fog` those
 * still in the fog; a scene with no learner line never opens.
 */
export function turnState(
  lines: readonly TurnLine[], words: Readonly<Record<string, { fog?: true }>>,
): { open: boolean; words: number; fog: number } {
  const keys = new Set<string>();
  let mine = 0;
  for (const l of lines) {
    if (!isYou(l.speaker)) continue;
    mine++;
    for (const p of [...(l.parts ?? []), ...(l.latinParts ?? [])]) if (p.key && words[p.key]) keys.add(p.key);
    for (const p of l.patterns ?? []) if (words[p.key]) keys.add(p.key);
  }
  const fog = [...keys].filter((k) => words[k].fog).length;
  return { open: mine > 0 && keys.size > 0 && fog === 0, words: keys.size, fog };
}
